import { Package, Plus, Trash } from 'shared/icons';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { MONO_CLASS } from '../../ui/Mono';
import Select from '../../ui/Select';
import { LocationPicker } from '../lpnPickers';
import QtyLines from './QtyLines';
import { DESTINATION_EXISTING, DESTINATION_NEW } from './repackModel';

/**
 * O DESTINO de um repack: um pallet que JÁ EXISTE (código bipado/digitado) ou
 * um pallet NOVO, criado na hora.
 *
 * ── A regra do §6.2 que aparece aqui ────────────────────────────────────────
 * **Só o SUPERVISOR cria LPN**, inclusive no repack manual. `canCreateLpn` vem
 * da permissão (`wms:manage:repack`); sem ela, a opção "pallet novo" não é
 * oferecida e a tela EXPLICA por quê, em vez de deixar o usuário descobrir com
 * um 403 depois de preencher o formulário inteiro. No coletor a opção nem
 * existe — lá o destino já tem que estar etiquetado.
 *
 * O pallet novo pede o mesmo que o recebimento do M3 (prefixo do cadastro,
 * posição viva e depósito ativo); o código sai da sequência do dia no servidor,
 * e a etiqueta é impressa no fim da operação.
 */
const DestinationEditor = ({
    label,
    destination,
    onChange,
    onRemove = null,
    canCreateLpn = false,
    prefixes,
    deposits,
    locations,
    showLines = true,
    title = null,
    disabled = false,
}) => {
    const isNew = destination.mode === DESTINATION_NEW;

    const changeLine = (key, qtyText) => {
        onChange({ lines: destination.lines.map((line) => (line.key === key ? { ...line, qtyText } : line)) });
    };

    return (
        <div className="space-y-3 rounded-xl border border-mono-200 p-4 dark:border-mono-800">
            <div className="flex items-center gap-2">
                {isNew ? (
                    <Plus size={18} className="shrink-0 text-mono-500" />
                ) : (
                    <Package size={18} className="shrink-0 text-mono-500" />
                )}
                <h3 className="flex-1 text-sm font-bold text-black dark:text-white">
                    {title || label('wms.repack.destination')}
                </h3>
                {onRemove ? (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onRemove}
                        disabled={disabled}
                        aria-label={label('wms.repack.remove_destination')}
                        title={label('wms.repack.remove_destination')}
                    >
                        <Trash size={16} />
                    </Button>
                ) : null}
            </div>

            <fieldset className="space-y-1.5">
                <legend className="text-xs font-medium text-mono-700 dark:text-mono-300">
                    {label('wms.repack.destination_mode')}
                </legend>
                <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm text-black dark:text-white">
                        <input
                            type="radio"
                            name={`destination-mode-${destination.key}`}
                            checked={!isNew}
                            onChange={() => onChange({ mode: DESTINATION_EXISTING })}
                            disabled={disabled}
                        />
                        {label('wms.repack.destination_existing')}
                    </label>
                    <label
                        className={`flex items-center gap-2 text-sm ${
                            canCreateLpn ? 'text-black dark:text-white' : 'text-mono-400 dark:text-mono-600'
                        }`}
                    >
                        <input
                            type="radio"
                            name={`destination-mode-${destination.key}`}
                            checked={isNew}
                            onChange={() => onChange({ mode: DESTINATION_NEW })}
                            disabled={disabled || !canCreateLpn}
                        />
                        {label('wms.repack.destination_new')}
                    </label>
                </div>
                {canCreateLpn ? null : (
                    <p className="text-xs text-mono-500 dark:text-mono-400">
                        <strong>{label('wms.repack.new_lpn_forbidden_title')}.</strong>{' '}
                        {label('wms.repack.new_lpn_forbidden_body')}
                    </p>
                )}
            </fieldset>

            {isNew ? (
                <div className="space-y-3">
                    <p className="text-xs text-mono-500 dark:text-mono-400">
                        {label('wms.repack.destination_new_hint')}
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <Select
                            label={label('wms.lpn_create.prefix')}
                            value={destination.prefixCode}
                            onChange={(event) => onChange({ prefixCode: event.target.value })}
                            disabled={disabled || prefixes.loading}
                            className={MONO_CLASS}
                        >
                            <option value="">—</option>
                            {prefixes.rows.length === 0 && !prefixes.loading ? (
                                <option value="" disabled>
                                    {label('wms.lpn_create.no_prefixes')}
                                </option>
                            ) : null}
                            {prefixes.rows.map((row) => (
                                <option key={row.id} value={row.code}>
                                    {`${row.code} — ${row.label}`}
                                </option>
                            ))}
                        </Select>
                        <Select
                            label={label('wms.lpn.field.deposit')}
                            value={destination.depositId}
                            onChange={(event) => onChange({ depositId: event.target.value })}
                            disabled={disabled || deposits.loading}
                        >
                            <option value="">—</option>
                            {deposits.rows.length === 0 && !deposits.loading ? (
                                <option value="" disabled>
                                    {label('wms.lpn_create.no_deposits')}
                                </option>
                            ) : null}
                            {deposits.rows.map((row) => (
                                <option key={row.id} value={String(row.id)}>
                                    {`${row.code} — ${row.label}`}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <LocationPicker
                        label={label}
                        locations={locations.rows}
                        loading={locations.loading}
                        value={destination.locationId}
                        onChange={(value) => onChange({ locationId: value })}
                        disabled={disabled}
                        emptyOptionText={label('wms.lpn_create.select_location')}
                    />
                    <label className="flex items-center gap-2 text-sm text-black dark:text-white">
                        <input
                            type="checkbox"
                            checked={destination.isBulky === true}
                            onChange={(event) => onChange({ isBulky: event.target.checked ? true : null })}
                            disabled={disabled}
                        />
                        {label('wms.lpn.field.is_bulky')}
                    </label>
                </div>
            ) : (
                <Input
                    label={label('wms.repack.lpn_code')}
                    value={destination.code}
                    onChange={(event) => onChange({ code: event.target.value.toUpperCase() })}
                    placeholder="REV-260811-002"
                    autoComplete="off"
                    disabled={disabled}
                    className={MONO_CLASS}
                />
            )}

            {showLines ? (
                <QtyLines
                    label={label}
                    lines={destination.lines}
                    onChangeQty={changeLine}
                    disabled={disabled}
                    emptyText={label('wms.repack.no_items')}
                />
            ) : null}
        </div>
    );
};

export default DestinationEditor;
